import { addMinutes, isBefore } from 'date-fns';
import { ListVariant } from '../_shared/types/trade';
import Singleton from '../Singleton';
import { localStorageService } from './StorageService';
import localStorage, {
  LOCALSTORAGE_EXPIRY_MINUTES as localStorageExpiryMinutes
} from '../_shared/constants/localStorage';
import CoreAppService from '../_shared/api/coreAppService';
import { UserListViewPreferenceType } from '../global.d';

export default class UserPreferencesService extends Singleton<
  UserPreferencesService
> {
  /**
   * Obtains the list variant from local storage. Defaults to list.
   * Required to determine initial redux state for listVariant.
   */
  public initializeListVariant = () => {
    return (
      localStorageService.getItem(localStorage.LIST_VARIANT)?.variant ??
      ListVariant.LIST
    );
  };

  /**
   * Stores the list variant in local storage with an expiry time.
   * */
  private storeListVariant = (variant: string) => {
    const expiryTime = addMinutes(
      new Date(),
      localStorageExpiryMinutes[localStorage.LIST_VARIANT]
    );
    localStorageService.setItem(localStorage.LIST_VARIANT, {
      variant,
      expiryTime
    });
  };

  /**
   * Checks if the list variant in local storage is expired. If it is, makes
   * an API call to get fresh data.
   */
  public getListVariant = async () => {
    const listVariant = localStorageService.getItem(localStorage.LIST_VARIANT);
    if (
      listVariant &&
      isBefore(
        new Date(),
        new Date(
          localStorageService.getItem(localStorage.LIST_VARIANT)?.expiryTime
        )
      )
    ) {
      return listVariant.variant;
    }
    const client = await CoreAppService();
    let preferredVariant;
    try {
      const listVariantData = await client.user.list();
      preferredVariant = listVariantData.data.listView;
    } catch (exp) {
      preferredVariant = ListVariant.LIST;
    }
    this.storeListVariant(preferredVariant);
    return preferredVariant;
  };

  /**
   * Updates the list variant user preference and stores it in local storage
   * */
  public updateListVariant = async (variant: UserListViewPreferenceType) => {
    this.storeListVariant(variant);
    const client = await CoreAppService();
    client.user.update({
      listView: variant
    });
  };
}
